import { AudienceGuard } from "@/components/AudienceGuard";
import { BookSessionErrorFallback } from "@/components/BookSessionErrorFallback";
import { ClientInformationForm } from "@/components/ClientInformationForm";
import { LanguageSwitcher } from "@/components/LanguageSwitcher";
import ExitExerciseModal from "@/components/ui/models/exitExerciseModal";
import BottomNavbar from "@/components/ui/navbar.tsx";
import TrainerNavbar from "@/components/trainer/TrainerNavbar";
import { Overlay } from "@/components/ui/overlay";
import { useLocale } from "@/contexts/IntlContext";
import { isAdultExerciseGroup } from "@/lib/data/contentCatalog";
import { useAuthStore } from "@/lib/stores/authStore";
import { useChildSessionStore } from "@/lib/stores/childSessionStore";
import { useExerciseStore } from "@/lib/stores/exercisesStore";
import { usePathModeStore } from "@/lib/stores/pathModeStore";
import { usePositiveImageryStore } from "@/lib/stores/positiveImageryStore";
import { cn } from "@/lib/utils";
import {
  createFileRoute,
  Outlet,
  useLocation,
  useMatches,
  useNavigate,
} from "@tanstack/react-router";
import { NotificationBanner } from "@/components/NotificationBanner";
import { motion } from "framer-motion";
import { ArrowSquareLeft, ArrowSquareRight } from "iconsax-reactjs";
import { useEffect, useState } from "react";
import { IoNotifications, IoNotificationsOff } from "react-icons/io5";
import { LuHeartHandshake } from "react-icons/lu";
import { FormattedMessage, useIntl } from "react-intl";
import * as Sentry from "@sentry/react";

export const Route = createFileRoute("/(content)")({ component: RouteComponent });

function RouteComponent() {
  const navigate = useNavigate();
  const matches = useMatches();
  const location = useLocation();
  const { user } = useAuthStore();

  const userAudience = user?.audience;
  const audiencePrefix = userAudience === "CHILD" ? "/children" : "/adults";

  const pathMode = usePathModeStore((s) => s.mode);
  const isTrainerMode =
    user?.accountType === "TRAINER" || (!user && pathMode === "TRAINER");

  const intl = useIntl();
  const { locale } = useLocale();
  const isRTL = locale === "ar";
  const [isBookSessionOpen, setIsBookSessionOpen] = useState(false);
  const [openExitExerciseModal, setOpenExitExerciseModal] = useState(false);
  const removeProgress = useExerciseStore((s) => s.removeProgress);
  // find better way to reset positive imagery store
  const { reset } = usePositiveImageryStore();

  const pathname = location.pathname;

  const isExercisePath = (path: string) => {
    const segments = path.split("/").filter(Boolean);
    if (segments.length < 2) return false;
    const [audienceSegment, exercisesSegment] = segments;
    if (!["adults", "children"].includes(audienceSegment)) return false;
    if (exercisesSegment !== "exercises") return false;

    const categoryOrSlug = segments[2];
    const CATEGORY_SLUGS = [
      "anger",
      "anxiety",
      "concentration",
      "depression",
      "guilt-feelings",
      "physical-pain",
      "self-inflicted-injuries",
      "sleep",
      "substance-use",
      "suicide-ideation",
      "trauma",
    ];

    const isCategoryList =
      CATEGORY_SLUGS.includes(categoryOrSlug) && segments.length === 3;

    return !isCategoryList && segments.length >= 3;
  };

  const handleBookSessionClick = () => {
    setIsBookSessionOpen(true);
  };

  useEffect(() => {
    const openBookSession = () => setIsBookSessionOpen(true);
    window.addEventListener("hamd:book-free-session", openBookSession);
    return () => window.removeEventListener("hamd:book-free-session", openBookSession);
  }, []);

  useEffect(() => {
    const openGetHelp = (event: Event) => {
      const detail = event instanceof CustomEvent ? event.detail : undefined;
      const category = detail?.category;
      if (
        typeof sessionStorage !== "undefined" &&
        (category === "children" || category === "humanitarian" || category === "legal" || category === "medical")
      ) {
        sessionStorage.setItem("hamd-get-help-default-category", category);
      }
      navigate({ to: "/get-help" });
    };
    window.addEventListener("hamd:get-help", openGetHelp);
    return () => window.removeEventListener("hamd:get-help", openGetHelp);
  }, [navigate]);

  const handleCloseBookSession = () => {
    setIsBookSessionOpen(false);
  };

  useEffect(() => {
    window.dispatchEvent(new CustomEvent("hamd:chatbot-visibility", {
      detail: { hidden: isBookSessionOpen },
    }));
    return () => {
      window.dispatchEvent(new CustomEvent("hamd:chatbot-visibility", {
        detail: { hidden: false },
      }));
    };
  }, [isBookSessionOpen]);
  // close is the button
  const handleCloseExerciseModal = () => {
    const exerciseId = getCurrentExerciseId(matches);
    if (exerciseId) {
      reset();
      removeProgress(exerciseId);
    }
    setOpenExitExerciseModal(false);

    handleBackClick();
  };

  // exist is the x icon
  const handleExitExerciseModal = () => {
    setOpenExitExerciseModal(false);
  };

  const handleSaveExitExerciseModal = () => {
    setOpenExitExerciseModal(false);
    handleBackClick();
  };

  const getActiveTab = () => {
    const currentMatch = matches[matches.length - 1];
    const pathname = currentMatch?.pathname || "";

    if (pathname.startsWith("/exercises")) {
      return "exercises";
    } else if (pathname.startsWith("/profile")) {
      return "profile";
    } else if (pathname === "/get-help") {
      return "get-help";
    }

    return "exercises"; // Default fallback
  };

  const getPageTitle = () => {
    const currentMatch = matches[matches.length - 1];

    const titleKey = (currentMatch?.context as { title?: string })?.title;

    // Map English titles to translation keys for backward compatibility
    const titleMap: Record<string, string> = {
      // Main Categories
      Exercises: "exercises.title",
      "Do you feel stressed? ": "stress.title",
      "Are you scared?": "fear.title",
      Movement: "weakness.title",
      "Do you feel weak?": "weakness.title",

      // Breathing Exercise
      "Guided Relaxation & Breathing Exercise": "breathing.title",
      "Step 1: Preparation": "breathing.step1.title",
      "Step 2: Breathing Prompts": "breathing.step2.title",
      "Step 3: Grounding Focus": "breathing.step3.title",
      "Step 4: Countdown (with breathing)": "breathing.step4.title",
      "Step 5: Optional Eye Closure ": "breathing.step5.title",
      "Step 6: Countdown to Calmness": "breathing.step6.title",
      "Step 7: Conclusion": "breathing.step7.title",

      // Personal Sources of Strength
      "Personal Sources of Strength": "personalSources.title",
      "Good memories": "personalSources.step1.title",
      "Positive attributes and characteristics": "personalSources.step2.title",
      "Upbringing and values from caregivers": "personalSources.step3.title",
      Abilities: "personalSources.step4.title",
      "Strategies for overcoming challenges": "personalSources.step5.title",
      "Knowledge and life experience": "personalSources.step6.title",
      "Hobbies and interests": "personalSources.step7.title",
      "Faith and beliefs": "personalSources.step8.title",
      "Wishes and visions": "personalSources.step9.title",
      Support: "personalSources.step10.title",
      "Role models": "personalSources.step11.title",
      "Reflection Summary": "personalSources.summary.title",

      // Strengths Map Exercise
      "My Strengths Map": "strengthsMap.title",
      "Discover Your Inner Powers": "strengthsMap.step1.title",
      "What Are Strengths?": "strengthsMap.step2.title",
      "Building the Map": "strengthsMap.step3.title",
      "Choose Your Strengths": "strengthsMap.step4.title",
      "Reflect on Your Strengths": "strengthsMap.step5.title",
      "Decorate the Wheel": "strengthsMap.step6.title",
      "Final Reflections": "strengthsMap.step7.title",

      "Finger Breathing Exercise": "breathing.fingerBreath.title",
      "Traveling Through Your Body": "stress.exercises.bodyExercise",
      "Relaxation Exercise": "weakness.exercises.relaxation",
      Content: "content.title",
      "Conversing Together": "conversingTogether.title",
      "Positive Imagery": "positiveImagery.title",
      "Imagination Exercise": "imagination.title",
      "Tree of Life": "treeOfLife.title",
      "Drawing Exercise": "drawing.title",
      "Humming Bee Breath": "hummingBee.title",
      "Circle of Control": "circleControl.title",
      "Draw My Happy Place": "happy-place.title",
    };

    // If we have a translation key, use it
    if (titleKey && titleMap[titleKey]) {
      return intl.formatMessage({ id: titleMap[titleKey] });
    }

    // If it's already a translation key
    if (
      titleKey &&
      (titleKey.startsWith("breathing.") ||
        titleKey.startsWith("personalSources.") ||
        titleKey.startsWith("exercises.") ||
        titleKey.startsWith("stress.") ||
        titleKey.startsWith("fear.") ||
        titleKey.startsWith("weakness") ||
        titleKey.startsWith("weakness.") ||
        titleKey.startsWith("profile.") ||
        titleKey.startsWith("content.") ||
        titleKey.startsWith("imagination.") ||
        titleKey.startsWith("conversingTogether.") ||
        titleKey.startsWith("positiveImagery.") ||
        titleKey.startsWith("treeOfLife.") ||
        titleKey.startsWith("drawing.") ||
        titleKey.startsWith("hummingBee.") ||
        titleKey.startsWith("angerIceberg.") ||
        titleKey.startsWith("myBraveStory.") ||
        titleKey.startsWith("groundingWalk.") ||
        titleKey.startsWith("circleControl.") ||
        titleKey.startsWith("happy-place.") ||
        titleKey.startsWith("strengthsMap.") ||
        titleKey.startsWith("study.") ||
        titleKey.startsWith("painfulMemories.") ||
        titleKey.startsWith("pleasantMemories.") ||
        titleKey.startsWith("threeBreath.") ||
        titleKey.startsWith("neurogenicTremoring.") ||
        titleKey.startsWith("butterflyHug.") ||
        titleKey.startsWith("waterfall.") ||
        titleKey.startsWith("baggage.") ||
        titleKey.startsWith("innerGarden.") ||
        titleKey.startsWith("oneFive.") ||
        titleKey.startsWith("getHelp.") ||
        titleKey.startsWith("eft.")
      )

    ) {
      return intl.formatMessage({ id: titleKey });
    }

    // Fallback to original title or default
    return titleKey || "HAMD";
  };

  const handleBackClick = () => {
    const currentMatch = matches[matches.length - 1];

    const pathname = currentMatch?.pathname || "";
    const search = currentMatch?.search as {
      saved?: boolean | string;
      referrer?: string;
      referrerGroup?: string;
      group?: string;
      mode?: string;
      step?: string;
    };

    if (
      pathname === "/children/exercises" ||
      pathname === "/children/exercises/" ||
      pathname === "/adults/exercises" ||
      pathname === "/adults/exercises/"
    ) {
      navigate({ to: "/" });
      return;
    }

    if (pathname === "/profile") {
      navigate({
        to:
          userAudience === "CHILD"
            ? "/children/exercises"
            : "/adults/exercises",
      });
      return;
    }

    if (pathname === "/get-help") {
      navigate({
        to: isTrainerMode
          ? "/trainer/courses"
          : userAudience === "CHILD"
            ? "/children/exercises"
            : "/adults/exercises",
      });
      return;
    }

    if (pathname === "/study" || pathname === "/study/") {
      if (search?.step === "consent") {
        navigate({ to: "/study/", search: { step: "info" } });
      } else {
        navigate({
          to:
            userAudience === "CHILD"
              ? "/children/exercises"
              : "/adults/exercises",
        });
      }
      return;
    }

    if (
      pathname ===
      `${audiencePrefix}/exercises/diary-adults/archive` &&
      search?.saved !== undefined
    ) {
      navigate({
        to: `${audiencePrefix}/exercises/diary-adults/exercise-explanation`,
      });
      return;
    }

    if (
      pathname === `${audiencePrefix}/exercises/diary-adults/exercise`
    ) {
      navigate({
        to: `${audiencePrefix}/exercises/diary-adults/exercise-explanation`,
      });
      return;
    }

    if (
      pathname ===
      `${audiencePrefix}/exercises/conversing-together/exercise`
    ) {
      if (!openExitExerciseModal) {
        setOpenExitExerciseModal(true);
        return;
      }
      setOpenExitExerciseModal(false);
    }

    if (
      pathname ===
      `${audiencePrefix}/exercises/positive-imagery/exercise`
    ) {
      if (!openExitExerciseModal) {
        setOpenExitExerciseModal(true);
        return;
      }
      setOpenExitExerciseModal(false);
    }

    if (
      userAudience === "CHILD" &&
      (pathname === `${audiencePrefix}/exercises/weakness/diary-children` ||
        pathname === `${audiencePrefix}/exercises/weakness/diary-children/exercise` ||
        pathname === `${audiencePrefix}/exercises/weakness/diary-children/exercise-explanation` ||
        pathname === `${audiencePrefix}/exercises/weakness/diary-children/diary-template`)
    ) {

      if (!openExitExerciseModal) {
        setOpenExitExerciseModal(true);
        return;
      }
      setOpenExitExerciseModal(false);
      return navigate({ to: `${audiencePrefix}/exercises/weakness` });
    }

    const ADULT_CATEGORY_SLUGS = [
      "anger", "anxiety", "concentration", "depression", "guilt-feelings",
      "physical-pain", "self-inflicted-injuries", "sleep", "substance-use",
      "suicide-ideation", "trauma", "nightmares", "flashbacks",
    ];
    const pathSegments = pathname.split("/").filter(Boolean);
    const isAdultCategoryPage =
      pathSegments.length === 3 &&
      pathSegments[0] === "adults" &&
      pathSegments[1] === "exercises" &&
      ADULT_CATEGORY_SLUGS.includes(pathSegments[2]);
    if (isAdultCategoryPage && !search?.referrer) {
      if (search?.mode === "videos") {
        navigate({ to: pathname, search: () => ({}) });
        return;
      }
      if (search?.mode === "exercises") {
        if (search?.group) {
          navigate({
            to: pathname,
            search: () => ({ mode: "exercises" as const }),
          });
        } else {
          navigate({ to: pathname, search: () => ({}) });
        }
        return;
      }
      if (search?.group) {
        navigate({
          to: pathname,
          search: () => ({ mode: "exercises" as const }),
        });
        return;
      }
    }
    if (
      pathname === `/adults/exercises/drawing/mandala` ||
      pathname === `/adults/exercises/drawing/summary`
    ) {
      navigate({
        to: "/adults/exercises/drawing",
        search: () => ({
          referrer: search?.referrer,
          referrerGroup: search?.referrerGroup,
        }),
      });
      return;
    }

    // Handle all shared exercises with referrer parameter
    if (search?.referrer) {
      const referrer = search.referrer as string;
      const referrerGroup = isAdultExerciseGroup(search.referrerGroup)
        ? search.referrerGroup
        : undefined;
      if (search?.referrerGroup) {
        navigate({
          to: `${audiencePrefix}/exercises/${referrer}`,
          search: () => ({ group: referrerGroup }),
        });
      } else {
        navigate({ to: `${audiencePrefix}/exercises/${referrer}` });
      }
      return;
    }

    const { selectedEmotionId, setStep: setChildSessionStep } =
      useChildSessionStore.getState();
    if (selectedEmotionId && pathname.startsWith("/children/exercises/")) {
      setChildSessionStep("exercise");
      navigate({ to: "/children/session" });
      return;
    }

    return navigate({ to: ".." });
  };

  const isBackButtonDisabled = () => {
    const currentMatch = matches[matches.length - 1];
    const pathname = currentMatch?.pathname || "";

    return (
      !!(
        pathname.includes("/personal-sources/") &&
        pathname.match(/\/personal-sources\/\d+$/)
      ) ||
      (pathname.endsWith("/summary") && !pathname.includes("/drawing/summary")) ||
      !!pathname.match(/\/summary\/[^/]+$/) ||
      pathname.endsWith("/end")
    );
  };

  const allowedPaths = [
    "/adults/exercises",
    "/children/exercises",
    "/profile",
    "/get-help",
  ];

  const shouldShowFloatingMenu = allowedPaths.includes(pathname);
  const isDrawingBoardPage = pathname.includes('/happy-place/drawing-board');
  const currentMatch = matches[matches.length - 1];
  const navbarConfig = (currentMatch?.context as any)?.navbar ?? {};
  const isTransparent = navbarConfig.transparent ?? false;
  const showTitle = navbarConfig.showTitle ?? false;
  const showOnBack = navbarConfig.onBack ?? true;
  const language = navbarConfig.languageSwitcher ?? false;
  const showTopbar = navbarConfig.showTopbar ?? true;

  const isExercisePage = isExercisePath(pathname);
  const isProfilePage = pathname === '/profile';
  const isStudyPage = pathname.startsWith("/study");
  const isSessionPage = pathname.startsWith("/children/session");
  // const isProfilePage = pathname === '/profile';
  const showNavbar = !isDrawingBoardPage && !isExercisePage && !isStudyPage && !isSessionPage;
  const textColor = isTransparent ? "text-white" : "text-primary";
  const iconColor = isTransparent ? "text-white" : "text-primary";

  const notifPermission: NotificationPermission | 'unsupported' =
    typeof Notification !== 'undefined' ? Notification.permission : 'unsupported';

  return (
    <AudienceGuard>
      <div className={`min-h-screen ${isDrawingBoardPage ? 'pb-4' : showNavbar ? 'pb-20' : 'pb-0'}`}>
        {showTopbar && (
          <section className={`relative`}>
            <div
              className={cn(
                "flex items-center justify-between p-4 pt-6  h-20 app-topbar",
                isTransparent ? "absolute top-0 bg-transparent z-20 w-full" : " relative bg-transparent"
              )}
            >
              <div className="m-0 p-0 h-[36px]">
                {showOnBack && (
                  <button
                    onClick={handleBackClick}
                    className={cn(isBackButtonDisabled() ? "opacity-0 pointer-events-none" : "cursor pointer")}
                    disabled={isBackButtonDisabled()}
                  >
                    {isRTL ? (
                      <ArrowSquareRight variant="Bold" size={36} className={iconColor} strokeWidth={3.5} />
                    ) : (
                      <ArrowSquareLeft variant="Bold" size={36} className={iconColor} strokeWidth={3.5} />
                    )}
                  </button>
                )}
              </div>

              {showTitle &&
                <div className="absolute left-1/2 transform -translate-x-1/2">
                  <h3 className={`text-h3 font-medium text-center select-none ${textColor}`}>
                    {getPageTitle()}
                  </h3>
                </div>
              }
              {language && (
                <div className="flex items-center gap-1">
                  <LanguageSwitcher className="w-9 h-9" />
                </div>
              )}
              {isProfilePage && notifPermission !== 'unsupported' && (
                <button
                  type="button"
                  className="p-1"
                  aria-label="Notifications"
                >
                  {notifPermission === 'granted'
                    ? <IoNotifications size={26} className={iconColor} />
                    : <IoNotificationsOff size={26} className="text-gray" />
                  }
                </button>
              )}
            </div>
          </section>
        )}

        {!isExercisePage && <NotificationBanner />}

        <div className={`${isDrawingBoardPage ? 'h-[calc(100vh-5rem)]' : showNavbar ? 'overflow-y-auto h-[calc(100%-11rem)]' : showTopbar ? 'overflow-y-auto h-[calc(100dvh-5rem)] min-h-0' : 'overflow-y-auto h-screen'}`}>
          <Outlet />
        </div>

        {showNavbar &&
          (isTrainerMode && pathname === "/get-help" ? (
            <TrainerNavbar activeTab="get-help" />
          ) : (
            <BottomNavbar activeTab={getActiveTab()} />
          ))}

        {shouldShowFloatingMenu && (
          <div className={`fixed bottom-[calc(5rem+env(safe-area-inset-bottom,0px))] right-0.5 md:bottom-2 md:right-2 z-10`}>
            <motion.div
              className=" overflow-hidden w-21 relative transition-colors"
              initial={{ opacity: 0, scale: 0.9 }}
              animate={{ opacity: 1, scale: 1 }}
              transition={{ duration: 0.3 }}
            >
              <motion.button
                onClick={handleBookSessionClick}
                className="w-full px-1 py-2 flex flex-col items-center justify-center"
                animate={{
                  scale: [1, 1.08, 1],
                  transition: {
                    duration: 2.4,
                    repeat: Infinity,
                    ease: "easeInOut",
                    repeatDelay: 0.6,
                  },
                }}
                whileHover={{ scale: 1.08 }}
                whileTap={{ scale: 0.94 }}
                transition={{ duration: 0.3 }}
              >
                <div className="w-10 h-10 rounded-full bg-primary flex items-center justify-center mb-1 shadow-lg">
                  <LuHeartHandshake size={24} className="text-white" />
                </div>
                <div className="text-[9px] font-medium text-white text-center leading-tight bg-primary p-1.5 rounded-lg shadow-lg">
                  <FormattedMessage
                    id={
                      isTrainerMode
                        ? "trainer.support.button"
                        : "client.booksession"
                    }
                    values={{ br: <br /> }}
                  />
                </div>
              </motion.button>
            </motion.div>
          </div>
        )}

        <Overlay show={isBookSessionOpen} onClose={handleCloseBookSession}>
          <Sentry.ErrorBoundary
            fallback={({ resetError }) => (
              <BookSessionErrorFallback
                onClose={() => {
                  resetError();
                  handleCloseBookSession();
                }}
              />
            )}
          >
            <ClientInformationForm
              onClose={handleCloseBookSession}
              variant={isTrainerMode ? "training" : "client"}
            />
          </Sentry.ErrorBoundary>
        </Overlay>

        <ExitExerciseModal
          openExitExerciseModal={openExitExerciseModal}
          closeExerciseModalCallback={handleCloseExerciseModal}
          existExerciseModalCallback={handleExitExerciseModal}
          saveExerciseModalCallback={handleSaveExitExerciseModal}
        />
      </div>
    </AudienceGuard>
  );
}

// Dynamic return of a path segment, ex: audiencePrefix/exercises/weakness/positive-imagery/exercise => positiveImagery
const getCurrentExerciseId = (matches: any) => {
  const currentMatch = matches[matches.length - 1];
  const pathname = currentMatch?.pathname || "";


  // Split by '/' and remove empty segments (handles leading/trailing slashes)
  const segments = pathname
    .split("/")
    .filter((segment: any) => segment.length > 0);

  // We need at least 4 segments to get the 4th one
  if (segments.length < 4) return null;

  // Get the 4th segment (index 3)
  let exerciseId = segments[3];

  // Convert kebab-case to camelCase if needed
  if (exerciseId.includes("-")) {
    exerciseId = exerciseId
      .split("-")
      .map((part, index) =>
        index === 0 ? part : part.charAt(0).toUpperCase() + part.slice(1)
      )
      .join("");
  }

  return exerciseId;
};
